import { z } from "zod";

/**
 * Zod schema for a single age reduction schedule entry.
 * Both fields must be present and within valid ranges.
 */
export const ageReductionEntrySchema = z.object({
  reductionPercent: z
    .number()
    .min(1, "Percentage must be between 1 and 100")
    .max(100, "Percentage must be between 1 and 100"),
  age: z
    .number()
    .min(1, "Age must be between 1 and 120")
    .max(120, "Age must be between 1 and 120"),
});

/**
 * Zod schema for the full age reduction schedule array.
 * At least one valid entry is required.
 */
export const ageReductionScheduleSchema = z
  .array(ageReductionEntrySchema)
  .min(1, "At least one schedule entry is required");

export interface AgeReductionEntry {
  id: string; // Stable identifier for React keys
  reductionPercent: number | string;
  age: number | string;
}

/**
 * Sort entries by age in ascending order
 */
export const sortEntriesByAge = (
  entriesToSort: AgeReductionEntry[],
): AgeReductionEntry[] =>
  [...entriesToSort].sort((a, b) => {
    const ageA = typeof a.age === "number" ? a.age : 0;
    const ageB = typeof b.age === "number" ? b.age : 0;
    return ageA - ageB;
  });

/**
 * Check if an entry is completely empty (both fields are empty)
 */
export const isEntryCompletelyEmpty = (entry: AgeReductionEntry): boolean => {
  const percentEmpty =
    entry.reductionPercent === "" ||
    entry.reductionPercent === null ||
    entry.reductionPercent === undefined;
  const ageEmpty =
    entry.age === "" || entry.age === null || entry.age === undefined;
  return percentEmpty && ageEmpty;
};

/**
 * Validate a single field value
 */
export const isFieldValid = (
  value: number | string,
  field: "reductionPercent" | "age",
): boolean => {
  if (value === "" || value === null || value === undefined) {
    return false;
  }

  const num = typeof value === "number" ? value : parseFloat(String(value));
  if (isNaN(num)) {
    return false;
  }

  if (field === "reductionPercent") {
    return num >= 1 && num <= 100;
  }
  // age
  return num >= 1 && num <= 120;
};

/**
 * Validate a complete entry (both fields must be valid)
 */
export const isEntryValid = (entry: AgeReductionEntry): boolean =>
  isFieldValid(entry.reductionPercent, "reductionPercent") &&
  isFieldValid(entry.age, "age");

/**
 * Get validation error message to display
 */
export const getValidationErrorMessage = (
  entries: AgeReductionEntry[],
): string | null => {
  const nonEmptyEntries = entries.filter((e) => !isEntryCompletelyEmpty(e));

  if (nonEmptyEntries.length === 0) {
    return "At least one schedule entry is required for this benefit type selection";
  }

  const hasInvalidEntries = nonEmptyEntries.some(
    (entry) => !isEntryValid(entry),
  );
  if (hasInvalidEntries) {
    return "Please enter valid values for all fields (percentage: 1-100, age: 1-120)";
  }

  return null;
};

/**
 * Check if Done button should be enabled
 * Enabled only if all non-empty entries are valid and at least one entry exists after filtering
 */
export const isDoneButtonEnabled = (entries: AgeReductionEntry[]): boolean => {
  const nonEmptyEntries = entries.filter((e) => !isEntryCompletelyEmpty(e));

  if (nonEmptyEntries.length === 0) {
    return false;
  }

  return nonEmptyEntries.every((entry) => isEntryValid(entry));
};
